/**
 * useIssueForm — Manages form state for create/edit issue flows.
 *
 * Handles: title, description, severity, type, screenshot URL,
 * form reset on open/close, clipboard paste for screenshots,
 * and selection-based screenshot updates.
 */
import { useState, useEffect } from 'react';
import { DEFAULT_ISSUE_TYPE } from '@overlayqa/shared';
import type { Issue, IssueSeverity, IssueType } from '@overlayqa/shared';
import type { SelectionRect } from '../SelectionOverlay';
import { log } from '@/lib/logger';

log.info('[useIssueForm] Module loaded');

export interface UseIssueFormOptions {
  isOpen: boolean;
  issue?: Issue;
  selection?: SelectionRect;
}

export interface UseIssueFormReturn {
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  severity: IssueSeverity;
  setSeverity: (v: IssueSeverity) => void;
  type: IssueType;
  setType: (v: IssueType) => void;
  screenshotUrl: string | null;
  setScreenshotUrl: (v: string | null) => void;
  assigneeId?: string | null;
  setAssigneeId: (value: string | null | undefined) => void;
  isCreating: boolean;
}

export function useIssueForm({
  isOpen,
  issue,
  selection,
}: UseIssueFormOptions): UseIssueFormReturn {
  log.info('[useIssueForm] Hook called', { isOpen, hasIssue: !!issue });

  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | null | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IssueSeverity>('medium');
  const [type, setType] = useState<IssueType>(DEFAULT_ISSUE_TYPE);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);

  const isCreating = !issue;

  // Reset form when drawer opens/closes or issue changes
  useEffect(() => {
    log.info('[useIssueForm] Reset effect', { isOpen, issueId: issue?.id });
    if (isOpen) {
      if (issue) {
        setTitle(issue.title);
        setAssigneeId(issue.assigneeId ?? null);
        setDescription(issue.description || '');
        setSeverity(issue.severity);
        setType(issue.type);
      } else {
        setTitle('');
        setAssigneeId(undefined);
        setDescription('');
        setSeverity('medium');
        setType(DEFAULT_ISSUE_TYPE);
      }
    }
  }, [isOpen, issue]);

  // Update screenshot when selection changes
  useEffect(() => {
    if (isOpen && isCreating) {
      log.info('[useIssueForm] Screenshot from selection', { hasImage: !!selection?.imageDataUrl });
      setScreenshotUrl(selection?.imageDataUrl || null);
    }
  }, [isOpen, isCreating, selection?.imageDataUrl]);

  // Handle paste for screenshots
  useEffect(() => {
    if (!isOpen || !isCreating) return;

    const handlePaste = (e: ClipboardEvent) => {
      log.info('[useIssueForm] Paste event received');
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            log.info('[useIssueForm] Image pasted, reading as data URL');
            const reader = new FileReader();
            reader.onload = (event) => {
              setScreenshotUrl(event.target?.result as string);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen, isCreating]);

  return {
    title,
    setTitle,
    description,
    setDescription,
    severity,
    setSeverity,
    type,
    setType,
    screenshotUrl,
    setScreenshotUrl,
    isCreating,
    assigneeId,
    setAssigneeId,
  };
}
